import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./DashboardKit.css";

// Shared building blocks for the customer and admin dashboards. Every widget renders the
// values it is given; nothing here fetches or invents data.

const numberFormat = new Intl.NumberFormat();
const compactFormat = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });

export const formatCount = (value) => numberFormat.format(Number(value) || 0);
export const formatCompact = (value) => {
  const number = Number(value) || 0;
  return Math.abs(number) < 10000 ? numberFormat.format(number) : compactFormat.format(number);
};

// Treat environments without matchMedia (tests, old browsers) like reduced motion: show final values.
export const prefersReducedMotion = () => typeof window === "undefined"
  || typeof window.matchMedia !== "function"
  || window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const useCountUp = (target, duration = 900) => {
  const value = Number(target) || 0;
  const [display, setDisplay] = useState(() => (prefersReducedMotion() ? value : 0));
  const displayRef = useRef(display);

  useEffect(() => {
    if (prefersReducedMotion()) {
      displayRef.current = value;
      setDisplay(value);
      return undefined;
    }
    const from = displayRef.current;
    const started = performance.now();
    let frame;
    const step = (now) => {
      const progress = Math.min(1, (now - started) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = Math.round(from + (value - from) * eased);
      displayRef.current = next;
      setDisplay(next);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return display;
};

export const AnimatedNumber = ({ value, format = formatCount, suffix = "" }) => {
  const display = useCountUp(value);
  return <span className="sx-number">{format(display)}{suffix}</span>;
};

const reveal = (index) => ({ "--sx-i": index || 0 });

export const DashboardHeader = ({ eyebrow, title, intro, status, statusTone = "live", actions }) => (
  <header className="sx-hero sx-reveal" style={reveal(0)}>
    <div className="sx-hero-copy">
      {eyebrow && <p className="sx-eyebrow">{eyebrow}</p>}
      <h1>{title}</h1>
      {intro && <p className="sx-intro">{intro}</p>}
    </div>
    <div className="sx-hero-side">
      {status && <div className={`sx-status-pill is-${statusTone}`}><span className="sx-status-dot" aria-hidden="true" />{status}</div>}
      {actions && <div className="sx-hero-actions">{actions}</div>}
    </div>
  </header>
);

export const RefreshButton = ({ onClick, busy, label = "Refresh" }) => (
  <button type="button" className={`sx-button is-ghost${busy ? " is-busy" : ""}`} onClick={onClick} disabled={busy} aria-label={busy ? "Refreshing data" : label}>
    <i className="ti ti-refresh" aria-hidden="true" /><span>{label}</span>
  </button>
);

export const StatCard = ({ icon, label, value, detail, tone = "teal", to, index = 0, meter }) => {
  const body = <>
    <div className="sx-stat-top">
      <span className={`sx-stat-icon tone-${tone}`} aria-hidden="true"><i className={`ti ${icon}`} /></span>
      {to && <i className="ti ti-arrow-up-right sx-stat-arrow" aria-hidden="true" />}
    </div>
    <div className="sx-stat-label">{label}</div>
    <div className="sx-stat-value"><AnimatedNumber value={value} /></div>
    {meter !== undefined && <div className={`sx-meter tone-${tone}`} aria-hidden="true"><span style={{ "--sx-fill": `${Math.max(0, Math.min(100, meter))}%` }} /></div>}
    {detail && <div className="sx-stat-detail">{detail}</div>}
  </>;
  const className = `sx-stat sx-reveal tone-${tone}${to ? " is-link" : ""}`;
  return to
    ? <Link to={to} className={className} style={reveal(index)}>{body}</Link>
    : <article className={className} style={reveal(index)}>{body}</article>;
};

export const Panel = ({ title, subtitle, icon, count, action, className = "", index = 0, children }) => (
  <section className={`sx-panel sx-reveal ${className}`} style={reveal(index)}>
    <div className="sx-panel-head">
      <div className="sx-panel-title">
        {icon && <span className="sx-panel-icon" aria-hidden="true"><i className={`ti ${icon}`} /></span>}
        <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
      </div>
      {count !== undefined && <span className="sx-count">{formatCount(count)}</span>}
      {action}
    </div>
    <div className="sx-panel-body">{children}</div>
  </section>
);

export const PanelLink = ({ to, children }) => <Link to={to} className="sx-panel-link">{children}<i className="ti ti-arrow-right" aria-hidden="true" /></Link>;

export const EmptyState = ({ icon = "ti-mood-empty", title, text, action }) => (
  <div className="sx-empty">
    <span className="sx-empty-icon" aria-hidden="true"><i className={`ti ${icon}`} /></span>
    <strong>{title}</strong>
    {text && <p>{text}</p>}
    {action}
  </div>
);

export const QuickActions = ({ actions }) => (
  <div className="sx-actions">
    {actions.map(({ to, icon, label, description, tone = "teal" }) => (
      <Link key={to} to={to} className="sx-action">
        <span className={`sx-action-icon tone-${tone}`} aria-hidden="true"><i className={`ti ${icon}`} /></span>
        <span className="sx-action-copy"><strong>{label}</strong>{description && <small>{description}</small>}</span>
        <i className="ti ti-chevron-right sx-action-arrow" aria-hidden="true" />
      </Link>
    ))}
  </div>
);

export const InsightList = ({ insights }) => (
  <ul className="sx-insights">
    {insights.map(({ id, tone = "info", icon, title, text, to, cta }) => (
      <li key={id} className={`sx-insight is-${tone}`}>
        <span className="sx-insight-icon" aria-hidden="true"><i className={`ti ${icon}`} /></span>
        <div>
          <strong>{title}</strong>
          {text && <p>{text}</p>}
          {to && cta && <Link to={to} className="sx-insight-link">{cta}<i className="ti ti-arrow-right" aria-hidden="true" /></Link>}
        </div>
      </li>
    ))}
  </ul>
);

const shortDay = (date) => new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
const sumCounts = (items) => items.reduce((total, item) => total + (Number(item.count) || 0), 0);

// Single-series daily bar chart. The title names the series, so there is no legend.
export const TrendChart = ({ title, subtitle, values, tone = "teal", index = 0 }) => {
  const [active, setActive] = useState(null);
  const counts = values.map((item) => Number(item.count) || 0);
  const maximum = Math.max(1, ...counts);
  const total = sumCounts(values);
  const half = Math.floor(values.length / 2);
  const recent = values.length >= 4 ? sumCounts(values.slice(half)) : null;
  const previous = values.length >= 4 ? sumCounts(values.slice(0, half)) : null;
  const delta = recent === null ? null : previous ? Math.round(((recent - previous) / previous) * 100) : recent ? 100 : 0;
  const shown = active === null ? null : values[active];

  return (
    <section className="sx-panel sx-trend sx-reveal" style={reveal(index)}>
      <div className="sx-panel-head">
        <div className="sx-panel-title"><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div></div>
      </div>
      <div className="sx-trend-summary">
        <div className="sx-trend-total">
          <span>{shown ? shortDay(shown.date) : "Period total"}</span>
          <strong>{shown ? formatCount(shown.count) : <AnimatedNumber value={total} />}</strong>
        </div>
        {delta !== null && !shown && <span className={`sx-delta ${delta > 0 ? "is-up" : delta < 0 ? "is-down" : "is-flat"}`} title="Second half of the period compared with the first half">
          <i className={`ti ${delta > 0 ? "ti-trending-up" : delta < 0 ? "ti-trending-down" : "ti-minus"}`} aria-hidden="true" />
          {delta > 0 ? "+" : ""}{delta}%
        </span>}
      </div>
      {values.length ? <>
        <div className={`sx-bars tone-${tone}`} style={{ "--sx-cols": values.length }} onMouseLeave={() => setActive(null)} role="img" aria-label={`${title}: ${formatCount(total)} in total over ${values.length} days`}>
          {values.map((item, position) => {
            const count = counts[position];
            const height = count ? Math.max(6, (count / maximum) * 100) : 2;
            return <div
              className={`sx-bar-col${active === position ? " is-active" : ""}`}
              key={item.date}
              onMouseEnter={() => setActive(position)}
              style={{ "--sx-delay": `${position * 28}ms` }}
            >
              <span className={`sx-bar${count ? "" : " is-zero"}`} style={{ "--sx-h": `${height}%` }} />
            </div>;
          })}
        </div>
        <div className="sx-bars-axis" aria-hidden="true">
          <span>{shortDay(values[0].date)}</span>
          {values.length > 1 && <span>{shortDay(values[values.length - 1].date)}</span>}
        </div>
        <table className="sx-sr-only">
          <caption>{title}</caption>
          <thead><tr><th scope="col">Date</th><th scope="col">Count</th></tr></thead>
          <tbody>{values.map((item) => <tr key={item.date}><td>{item.date}</td><td>{formatCount(item.count)}</td></tr>)}</tbody>
        </table>
      </> : <EmptyState icon="ti-chart-bar-off" title="No activity yet" text="Nothing was recorded for this period." />}
    </section>
  );
};

// Share-of-total list: each row carries its own label and value, so identity never relies on color.
export const ProportionList = ({ items }) => {
  const total = items.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
  return (
    <ul className="sx-proportions">
      {items.map(({ key, label, value, meta, icon, tone = "teal" }) => {
        const share = total ? Math.round(((Number(value) || 0) / total) * 100) : 0;
        return <li key={key || label}>
          <div className="sx-proportion-row">
            <span className="sx-proportion-label">{icon && <i className={`ti ${icon}`} aria-hidden="true" />}<span>{label}</span>{meta && <small>{meta}</small>}</span>
            <span className="sx-proportion-value"><strong><AnimatedNumber value={value} /></strong><small>{share}%</small></span>
          </div>
          <div className={`sx-meter is-thin tone-${tone}`} aria-hidden="true"><span style={{ "--sx-fill": `${share}%` }} /></div>
        </li>;
      })}
    </ul>
  );
};

export const DashboardSkeleton = ({ label, stats = 6 }) => (
  <div className="sx-skeleton" aria-busy="true">
    <p className="sx-sr-only" aria-live="polite">{label}</p>
    <div className="sx-stats">{Array.from({ length: stats }, (_, position) => <div key={position} className="sx-skeleton-block is-stat" />)}</div>
    <div className="sx-grid-2"><div className="sx-skeleton-block is-panel" /><div className="sx-skeleton-block is-panel" /></div>
  </div>
);
